import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Organization {
  id: string;
  name: string;
  logo_url: string | null;
  country: string | null;
  team_type?: string | null;
  is_beta?: boolean | null;
  stripe_account_id?: string | null;
  stripe_onboarding_complete?: boolean | null;
  camp_active?: boolean | null;
  camp_start_date?: string | null;
  camp_end_date?: string | null;
}

interface OrganizationContextType {
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentRole: string | null;
  isSuperAdmin: boolean;
  loading: boolean;
  switchOrganization: (orgId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const { toast } = useToast();

  const loadIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadOrganizations();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        loadOrganizations();
      } else if (event === 'SIGNED_OUT') {
        if (mountedRef.current) {
          setOrganizations([]);
          setCurrentOrganization(null);
          setCurrentRole(null);
          setIsSuperAdmin(false);
          setLoading(false);
        }
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const loadOrganizations = useCallback(async () => {
    const thisLoadId = ++loadIdRef.current;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (mountedRef.current) setLoading(false);
        return;
      }

      if (thisLoadId !== loadIdRef.current || !mountedRef.current) return;

      const { data: userRoles, error: rolesError } = await supabase
        .from("user_organization_roles")
        .select("organization_id, role")
        .eq("user_id", session.user.id);

      if (rolesError) throw rolesError;
      if (thisLoadId !== loadIdRef.current || !mountedRef.current) return;

      const hasSuperAdmin = userRoles?.some((r) => r.role === "super_admin") || false;

      const restrictedRoles = new Set(["parent", "player_viewer"]);
      const hasOnlyRestricted =
        !hasSuperAdmin &&
        !!userRoles &&
        userRoles.length > 0 &&
        userRoles.every((r) => restrictedRoles.has(r.role as string));

      let orgsData: any[] | null = null;
      let orgsError: any = null;

      if (hasOnlyRestricted) {
        // parent / player_viewer roles cannot SELECT organizations directly —
        // load via the safe RPC which strips invite_code / stripe_account_id.
        const { data, error } = await (supabase.rpc as any)("get_user_organizations_safe");
        orgsData = data;
        orgsError = error;
      } else {
        let orgsQuery = supabase
          .from("organizations")
          .select(
            "id, name, logo_url, country, team_type, is_beta, stripe_onboarding_complete, stripe_account_id, camp_active, camp_start_date, camp_end_date"
          )
          .order("name");

        if (!hasSuperAdmin && userRoles) {
          const orgIds = userRoles.map((r) => r.organization_id);
          if (orgIds.length === 0) {
            if (mountedRef.current) {
              setOrganizations([]);
              setCurrentOrganization(null);
              setCurrentRole(null);
              setIsSuperAdmin(false);
              setLoading(false);
            }
            return;
          }
          orgsQuery = orgsQuery.in("id", orgIds);
        }

        const res = await orgsQuery;
        orgsData = res.data;
        orgsError = res.error;
      }

      if (orgsError) throw orgsError;
      if (thisLoadId !== loadIdRef.current || !mountedRef.current) return;

      setIsSuperAdmin(hasSuperAdmin);

      const allOrgs = orgsData || [];
      setOrganizations(allOrgs);

      const savedOrgId = localStorage.getItem("currentOrganizationId");
      const validSaved = savedOrgId ? allOrgs.find((o) => o.id === savedOrgId) : null;
      const currentOrg = validSaved || allOrgs[0] || null;

      if (currentOrg) {
        setCurrentOrganization(currentOrg);
        localStorage.setItem("currentOrganizationId", currentOrg.id);
        const role = userRoles?.find((r) => r.organization_id === currentOrg.id)?.role || null;
        setCurrentRole(role);
      } else {
        setCurrentOrganization(null);
        setCurrentRole(null);
      }
    } catch (error: any) {
      if (mountedRef.current) {
        console.error("Error loading organizations:", error);
        toast({
          title: "Failed to load organizations",
          description: error?.message ?? "Unknown error",
          variant: "destructive",
        });
      }
    } finally {
      if (thisLoadId === loadIdRef.current && mountedRef.current) {
        setLoading(false);
      }
    }
  }, [toast]);

  const switchOrganization = useCallback(async (orgId: string) => {
    if (isSwitching) return;

    try {
      setIsSwitching(true);
      const org = organizations.find((o) => o.id === orgId);
      if (!org) throw new Error("Organization not found");

      setCurrentOrganization(org);
      localStorage.setItem("currentOrganizationId", orgId);

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: userRoles } = await supabase
          .from("user_organization_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .eq("organization_id", orgId)
          .limit(1)
          .maybeSingle();

        setCurrentRole(userRoles?.role || null);
      }

      // Audit log: record when a super_admin switches into another org
      if (isSuperAdmin && session) {
        try {
          await supabase.from("system_logs").insert({
            event: "super_admin_org_switch",
            action: "org_switch",
            level: "info",
            user_id: session.user.id,
            performed_by: session.user.id,
            organization_id: "6660fd33-58eb-4df7-bd64-17912444fed0",
            metadata: {
              target_org_id: orgId,
              target_org_name: org.name,
              accessed_at: new Date().toISOString(),
              access_reason: "platform_support",
            },
          });
        } catch (logErr) {
          console.error("Failed to log org switch audit entry:", logErr);
        }
      }

      toast({ title: "Organization switched", description: org.name });
      window.location.reload();
    } catch (error: any) {
      console.error("Error switching organization:", error);
      toast({
        title: "Failed to switch organization",
        description: error?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsSwitching(false);
    }
  }, [organizations, isSwitching, toast]);

  const refreshOrganizations = useCallback(async () => {
    await loadOrganizations();
  }, [loadOrganizations]);

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        currentOrganization,
        currentRole,
        isSuperAdmin,
        loading,
        switchOrganization,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
